import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { DataTable } from "@loot-os/ui/components/data-table";
import { Input } from "@loot-os/ui/components/input";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { createDataTableColumns } from "@loot-os/ui/lib/dataTable";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { ErrorAlert } from "../../components/ErrorAlert";
import { LoadMoreButton } from "../../components/LoadMoreButton";
import {
  type AccountingSpan,
  localDateInputValue,
  localDayRange,
  localMonthInputValue,
  localMonthRange,
} from "../../lib/accountingRange";
import { centsToDollars, formatDate } from "../../lib/format";
import { useTRPC } from "../../lib/trpc";

const PAGE_SIZE = 50;

type AccountingLine = {
  id: string;
  procurementId: string | null;
  catalogItemId: string | null;
  name: string | null;
  image: string | null;
  debitAccount: string;
  creditAccount: string;
  amountCents: number;
  description: string;
  createdAt: Date | string;
};

const accountingColumns = createDataTableColumns<AccountingLine>();
const ACCOUNTING_COLUMNS = accountingColumns.columns([
  accountingColumns.accessor("createdAt", {
    header: "When",
    size: 180,
    cell: ({ getValue }) => <span className="whitespace-nowrap">{formatDate(getValue())}</span>,
    meta: { filter: "text", csv: (value) => new Date(value as Date).toISOString() },
  }),
  accountingColumns.accessor((line) => line.name ?? "", {
    id: "item",
    header: "Item",
    size: 320,
    cell: ({ row }) => <ItemLabel name={row.original.name} image={row.original.image} />,
    meta: { filter: "text" },
  }),
  accountingColumns.accessor("debitAccount", {
    header: "Debit",
    size: 180,
    meta: { filter: "text" },
  }),
  accountingColumns.accessor("creditAccount", {
    header: "Credit",
    size: 180,
    meta: { filter: "text" },
  }),
  accountingColumns.accessor("amountCents", {
    header: "Amount",
    size: 110,
    cell: ({ getValue }) => centsToDollars(getValue()),
    meta: {
      align: "end",
      filter: "number",
      filterCents: true,
      csv: (value) => String(Number(value) / 100),
    },
  }),
  accountingColumns.accessor("description", {
    header: "Description",
    size: 240,
    meta: { filter: "text" },
  }),
]);

function signedDollars(cents: number): string {
  const amount = `$${Math.abs(cents / 100).toFixed(2)}`;
  return cents < 0 ? `-${amount}` : amount;
}

/**
 * Accounting lines for one local day or month: the items that moved, a download of
 * that range, and the total debited, credited and net in each bucket.
 */
export function AccountingUnitsPanel() {
  const trpc = useTRPC();
  const [span, setSpan] = useState<AccountingSpan>("day");
  const [day, setDay] = useState(() => localDateInputValue(new Date()));
  const [month, setMonth] = useState(() => localMonthInputValue(new Date()));
  const range = span === "day" ? localDayRange(day) : localMonthRange(month);
  const from = range?.from.toISOString();
  const to = range?.to.toISOString();

  const totals = useQuery(
    trpc.platform.fulfillment.procurement.units.accountingTotals.queryOptions(
      { from: from ?? "", to: to ?? "" },
      { enabled: from != null && to != null },
    ),
  );
  const lines = useInfiniteQuery(
    trpc.platform.fulfillment.procurement.units.accounting.infiniteQueryOptions(
      { from: from ?? "", to: to ?? "", limit: PAGE_SIZE },
      { enabled: from != null && to != null, getNextPageParam: (last) => last.nextCursor },
    ),
  );
  const rows = lines.data?.pages.flatMap((page) => page.lines) ?? [];
  const rangeLabel = span === "day" ? day : month;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Accounting</CardTitle>
        <CardDescription>
          Units that moved value on a local day or in a local month. Each bucket is the total
          debited, credited, and the net of the two.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant={span === "day" ? "default" : "outline"}
            onClick={() => setSpan("day")}
          >
            Day
          </Button>
          <Button
            size="sm"
            variant={span === "month" ? "default" : "outline"}
            onClick={() => setSpan("month")}
          >
            Month
          </Button>
          {span === "day" ? (
            <Input
              type="date"
              aria-label="Accounting day"
              className="w-44"
              value={day}
              onChange={(event) => setDay(event.target.value)}
            />
          ) : (
            <Input
              type="month"
              aria-label="Accounting month"
              className="w-44"
              value={month}
              onChange={(event) => setMonth(event.target.value)}
            />
          )}
        </div>

        {totals.isError ? <ErrorAlert message={totals.error.message} /> : null}
        {totals.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : totals.data ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bucket</TableHead>
                  <TableHead>Debited</TableHead>
                  <TableHead>Credited</TableHead>
                  <TableHead>Net</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {totals.data.map((bucket) => (
                  <TableRow key={bucket.account}>
                    <TableCell>{bucket.account}</TableCell>
                    <TableCell>{centsToDollars(bucket.debitedCents)}</TableCell>
                    <TableCell>{centsToDollars(bucket.creditedCents)}</TableCell>
                    <TableCell>{signedDollars(bucket.netCents)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : null}

        {lines.isError ? (
          <ErrorAlert message={lines.error.message} />
        ) : lines.isPending ? (
          <Skeleton className="h-40 w-full" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No accounting lines in this range.</p>
        ) : (
          <DataTable
            columns={ACCOUNTING_COLUMNS}
            data={rows}
            csvData={rows}
            csvName={`accounting-units-${rangeLabel}`}
            csvDisabled={lines.hasNextPage}
            csvDisabledReason="Load every page before downloading the complete range."
            emptyMessage="No accounting lines in this range."
            getRowId={(line) => line.id}
            maxHeight="32rem"
            search="Search loaded lines"
          />
        )}
        <LoadMoreButton
          hasNextPage={lines.hasNextPage}
          isFetchingNextPage={lines.isFetchingNextPage}
          onClick={() => void lines.fetchNextPage()}
        />
      </CardContent>
    </Card>
  );
}

function ItemLabel({ name, image }: { name: string | null; image: string | null }) {
  return (
    <div className="flex items-center gap-2">
      {image ? (
        <img src={image} alt="" className="h-10 w-10 shrink-0 object-contain" />
      ) : (
        <div className="h-10 w-10 shrink-0 rounded bg-muted" />
      )}
      <span className="min-w-0 truncate font-medium" title={name ?? undefined}>
        {name ?? "—"}
      </span>
    </div>
  );
}
